/*
 * Exercise B screen — FR-EXB-03 (#21).
 *
 * Renders one Exercise B question (the source content's Latin string
 * with one position blanked, plus up to 4 rune option tiles). On
 * answer:
 *   - records the user's selection (closure-local `answered` flag),
 *   - fills the blank slot with the chosen rune glyph (sage-green if
 *     correct, terracotta-red if wrong) — see "Blank-fill policy"
 *     below for the call we made,
 *   - marks the correct tile with `.option-tile--correct`,
 *   - marks the chosen tile with `.option-tile--wrong` if incorrect,
 *   - disables every option tile (no answer changes),
 *   - reveals the English translation of the source content (this is
 *     the FR-EXB-03 AC: the gloss is hidden until after the answer so
 *     it can't give the rune away),
 *   - plays the appropriate SFX via src/data/audio.ts (M5 stub today),
 *   - emits the FR-ANALYTICS-01 answer event with exerciseType='B',
 *   - reveals an explicit Next control (no auto-advance — FR-NAV-02).
 *
 * The Exit ("Back to levels") button is always visible (FR-NAV-02
 * carry-over from the placeholder). The screen is keyboard-accessible:
 * 1–4 select options directly; Up/Down/Left/Right move a focus ring
 * across the tiles and Enter selects the focused one.
 *
 * ---------------------------------------------------------------------
 * Mirror of exercise-a.ts:
 *
 * This screen deliberately mirrors src/screens/exercise-a.ts in
 * structure, idiom, and naming. The only behavioural deltas are:
 *   1. The "question" is rendered as a Latin string with a blank
 *      position (instead of a hero rune card), and the option tiles
 *      hold rune glyphs (instead of Latin labels).
 *   2. After answering, the blank slot is replaced with the rune
 *      glyph the user picked, and an English translation appears
 *      below.
 *   3. Analytics calls onAnswer with `'B'` not `'A'`.
 *   4. Next/cache-busting reloads exercise-b instead of exercise-a.
 *
 * Lifecycle, MutationObserver-based listener cleanup, parseLevel
 * digit-only guard, and the "unavailable" branches are byte-identical
 * in spirit to exercise-a.ts; see that file's top-of-file comment for
 * the why behind those choices.
 *
 * ---------------------------------------------------------------------
 * Blank-fill policy on answer:
 *
 * The mockup (design-system-C / Screen 07) shows the blank slot being
 * replaced with the RUNE GLYPH the user picked — green for correct,
 * red for wrong. We follow the mockup: filling with the rune glyph
 * (not the Latin equivalent) keeps the visual feedback consistent with
 * the "match shape to sound" framing of Exercise B (the user is
 * picking a glyph, so the answer-feedback shows the glyph they picked).
 *
 * On a wrong answer the chosen rune fills the blank in red and the
 * correct option tile is separately marked green, so the user can see
 * both their pick AND the right answer without us cluttering the Latin
 * string with two glyphs.
 *
 * ---------------------------------------------------------------------
 * Placeholder-content assumption (1:1 rune ↔ Latin position):
 *
 * The generator's `blankIndex` is an index into
 * `Array.from(source.rune)`. To highlight the matching position in the
 * Latin string, we need the corresponding index in
 * `Array.from(source.latin)`. The current `public/content/runes.csv` is
 * one rune glyph per Latin character (e.g. `"barâk"` ↔ five glyphs),
 * so the indices line up 1:1. Roy's real content may introduce
 * combined runes that cover multiple Latin characters; resolving that
 * mapping properly is deferred (his email reply is pending). For the
 * MVP we use the 1:1 assumption and clamp out-of-bounds indices to the
 * last Latin code-point, which is the closest sensible fallback.
 *
 * ---------------------------------------------------------------------
 * Scope boundaries:
 *
 * - Source/target picking is delegated to generateExerciseB; the
 *   screen never reaches into the corpus directly. M4 session
 *   orchestration (#23 / FR-LOGIC-02) will eventually drive the
 *   question stream and the `isReview` flag; today every render rolls
 *   a fresh random question.
 *
 * - Audio is the no-op stub in src/data/audio.ts. FR-AUDIO-01 (M5)
 *   replaces those bodies with real Audio() calls; the import
 *   boundary here stays put.
 *
 * - State across questions doesn't exist yet — every Next click is a
 *   navigate() back to this route, which re-runs render() and picks a
 *   fresh target. Scoring/streaks are M4.
 */

import { navigate } from '../router';
import { getCorpus } from '../data/csv';
import { generateExerciseB, type ExerciseBQuestion } from '../data/exercise-b';
import { onAnswer } from '../data/analytics';
import { playCorrect, playIncorrect } from '../data/audio';

export function renderExerciseB(mount: HTMLElement, params?: Record<string, string>): void {
  const level = parseLevel(params?.['n']);

  const section = document.createElement('section');
  section.dataset.testid = 'exercise-b';
  if (level !== null) section.dataset.level = String(level);

  // Invalid level → "unavailable" branch with just an exit button.
  if (level === null) {
    section.appendChild(renderUnavailable('Pick a level from the home screen to begin.'));
    section.appendChild(renderExitButton());
    mount.appendChild(section);
    return;
  }

  // Delegate everything (source, blank position, options, shuffle) to
  // the FR-EXB-01 generator. It returns null when the corpus has no
  // usable content for this level — graceful fall-through to the
  // unavailable card. M4 will replace this with session-driven calls.
  const corpus = getCorpus();
  const generated = generateExerciseB(corpus, level);
  if (generated === null) {
    section.appendChild(
      renderUnavailable(`No exercise content is configured for Level ${level} yet.`),
    );
    section.appendChild(renderExitButton());
    mount.appendChild(section);
    return;
  }
  // Re-bind to a non-null const so closures below (handleAnswer,
  // keyboard handler) get the narrowed type without TS losing
  // control-flow analysis across the closure boundary.
  const question = generated;

  // ---- Closure-local state ----
  // The render fn is called once per route entry; question + answered
  // flag live in this closure for the lifetime of the rendered DOM.
  let answered = false;
  let focusedIndex = 0;
  const tiles: HTMLButtonElement[] = [];
  let nextButton: HTMLButtonElement | null = null;
  let blankSpan: HTMLSpanElement | null = null;
  let translation: HTMLElement | null = null;

  function handleAnswer(idx: number): void {
    if (answered) return;
    if (idx < 0 || idx >= question.options.length) return;
    answered = true;

    const wasCorrect = idx === question.correctIndex;
    const chosenEntry = question.options[idx];

    // Fill the blank slot with the chosen rune's glyph. See the
    // "Blank-fill policy" note in the file header for why we use the
    // rune (not the Latin equivalent). Green/red styling is applied
    // via two state classes that mirror the design mockup.
    if (blankSpan && chosenEntry) {
      const glyph = Array.from(chosenEntry.rune)[0] ?? chosenEntry.rune;
      blankSpan.textContent = glyph;
      blankSpan.classList.remove('exercise-b__blank--empty');
      blankSpan.classList.add(
        wasCorrect ? 'exercise-b__blank--correct' : 'exercise-b__blank--wrong',
      );
      blankSpan.dataset.state = wasCorrect ? 'correct' : 'wrong';
    }

    // Always mark the actual correct answer; mark the chosen tile
    // wrong if it didn't match. Order matters: if the user picked
    // correctly, both "correct" branches resolve to the same tile, so
    // we only add the `--correct` class once.
    const correctTile = tiles[question.correctIndex];
    if (correctTile) correctTile.classList.add('option-tile--correct');
    if (!wasCorrect) {
      const chosenTile = tiles[idx];
      if (chosenTile) chosenTile.classList.add('option-tile--wrong');
    }

    // Disable all tiles so further clicks are inert (the closure-local
    // `answered` guard also catches keyboard input).
    for (const tile of tiles) {
      tile.disabled = true;
    }

    // Reveal the English translation — this is the FR-EXB-03 AC. It
    // was rendered hidden so it can't tip off the answer pre-commit.
    if (translation) {
      translation.hidden = false;
    }

    // Audio + analytics. Audio is a no-op stub (FR-AUDIO-01 / M5);
    // analytics is the real seam from FR-ANALYTICS-01.
    if (wasCorrect) {
      playCorrect();
    } else {
      playIncorrect();
    }
    // Use question.target.latin so the analytics payload always
    // identifies the rune the user was supposed to pick, even if the
    // generator someday substitutes a different target.
    onAnswer(question.target.latin, 'B', wasCorrect);

    // Reveal Next (it was hidden until the user committed to an
    // answer). Move focus to Next so keyboard users can press Enter to
    // advance.
    if (nextButton) {
      nextButton.hidden = false;
      nextButton.focus();
    }
  }

  // ---- Source card with the Latin string + blanked position ----
  const sourceCard = document.createElement('article');
  sourceCard.className = 'card card--elevated card--hero';
  sourceCard.dataset.testid = 'exercise-b-source';
  sourceCard.style.textAlign = 'center';

  const prompt = document.createElement('div');
  prompt.dataset.testid = 'exercise-b-prompt';
  prompt.style.color = 'var(--color-text-warm)';
  prompt.style.fontSize = 'var(--font-size-body-s)';
  prompt.style.textTransform = 'uppercase';
  prompt.style.letterSpacing = '0.1em';
  prompt.textContent = 'Which rune fits here?';
  sourceCard.appendChild(prompt);

  // Latin string with one blank slot. We decompose Latin into Unicode
  // code points with Array.from so that combining marks (e.g. `â`)
  // survive as single visual units. The blank index is mapped 1:1
  // from question.blankIndex per the placeholder-content assumption
  // documented in the file header.
  const sentence = document.createElement('div');
  sentence.dataset.testid = 'exercise-b-sentence';
  sentence.style.marginTop = 'var(--space-4)';
  sentence.style.fontFamily = 'var(--font-family-display)';
  sentence.style.fontSize = 'var(--font-size-display-s)';
  sentence.style.lineHeight = '1.3';

  const latinChars = Array.from(question.source.latin);
  // Clamp out-of-bounds defensively — the only known way this could
  // fire is a non-1:1 rune-to-Latin source from real content, which
  // is documented as a deferred edge case.
  const clampedBlankIdx = Math.min(question.blankIndex, Math.max(latinChars.length - 1, 0));

  for (let i = 0; i < latinChars.length; i++) {
    if (i === clampedBlankIdx) {
      const blank = document.createElement('span');
      blank.className = 'exercise-b__blank exercise-b__blank--empty';
      blank.dataset.testid = 'exercise-b-blank';
      blank.dataset.state = 'empty';
      // Screen-reader cue: a literal "_" is rendered as "underscore" by
      // some AT, and skipped by others. Label the slot explicitly so
      // the source prompt reads as "ba [blank] ak" rather than gibberish.
      blank.setAttribute('aria-label', 'blank');
      // Visual treatment lives in components.css (.exercise-b__blank +
      // --empty/--correct/--wrong modifiers). Keeping it class-based
      // means handleAnswer's class swap actually re-colors the slot;
      // inline styles previously bled through and broke the reveal.
      blank.textContent = '_';
      blankSpan = blank;
      sentence.appendChild(blank);
    } else {
      const char = document.createElement('span');
      char.textContent = latinChars[i]!;
      sentence.appendChild(char);
    }
  }
  sourceCard.appendChild(sentence);

  // English translation — hidden until the user commits an answer.
  // Rendering it now (just hidden) keeps the post-answer reveal cheap
  // and the layout stable.
  translation = document.createElement('div');
  translation.dataset.testid = 'exercise-b-translation';
  translation.style.marginTop = 'var(--space-3)';
  translation.style.color = 'var(--color-text-warm)';
  translation.style.fontStyle = 'italic';
  // aria-live='polite' so the gloss is announced to AT when handleAnswer
  // flips `hidden=false`. Without it the reveal is silent — the visual
  // feedback fires but screen-reader users get no signal that the
  // answer summary just appeared.
  translation.setAttribute('aria-live', 'polite');
  translation.textContent = `"${question.source.english}"`;
  translation.hidden = true;
  sourceCard.appendChild(translation);

  section.appendChild(sourceCard);

  // ---- Option tiles (rune glyphs) ----
  const optionList = document.createElement('div');
  optionList.dataset.testid = 'exercise-b-options';
  optionList.style.display = 'grid';
  optionList.style.gap = 'var(--space-3)';
  optionList.style.marginTop = 'var(--space-6)';

  question.options.forEach((option, idx) => {
    const tile = document.createElement('button');
    tile.type = 'button';
    tile.className = 'option-tile';
    tile.dataset.testid = `exercise-b-option-${idx}`;
    tile.dataset.optionIndex = String(idx);
    // Accessible name for screen readers: the tile's only visible
    // content is an index span and a rune glyph in a custom font. AT
    // can't read either as a sensible label, so without aria-label the
    // user hears "Option 1, [unintelligible glyph]". Latin is the
    // published per-rune transliteration and our best published name.
    tile.setAttribute('aria-label', `Option ${idx + 1}: ${option.latin}`);
    // Minimum touch-target (44×44) is met by the existing .option-tile
    // padding + body-l font-size; min-height here is belt-and-braces
    // in case the design tokens drift.
    tile.style.minHeight = '44px';

    const key = document.createElement('span');
    key.className = 'option-tile__key';
    key.textContent = String(idx + 1);
    // aria-hidden because the index is already in the aria-label above;
    // letting AT read it twice ("Option 1, 1, [glyph]") is noise.
    key.setAttribute('aria-hidden', 'true');
    tile.appendChild(key);

    // Each option is a rune glyph — the .rune class pulls in the
    // bespoke font. Use the first code point of `option.rune` to match
    // exercise-a.ts's defensive single-glyph extraction.
    const glyph = document.createElement('span');
    glyph.className = 'rune';
    glyph.textContent = Array.from(option.rune)[0] ?? option.rune;
    // Same rationale as the index: the glyph is also covered by
    // aria-label, and AT reading a custom-font codepoint is noise.
    glyph.setAttribute('aria-hidden', 'true');
    tile.appendChild(glyph);

    tile.addEventListener('click', () => {
      handleAnswer(idx);
    });

    tiles.push(tile);
    optionList.appendChild(tile);
  });

  section.appendChild(optionList);

  // ---- Action row: Next (hidden until answered) + Exit ----
  const actions = document.createElement('div');
  actions.dataset.testid = 'exercise-b-actions';
  actions.style.display = 'flex';
  actions.style.gap = 'var(--space-3)';
  actions.style.marginTop = 'var(--space-6)';

  nextButton = document.createElement('button');
  nextButton.type = 'button';
  nextButton.className = 'btn btn--primary';
  nextButton.dataset.testid = 'exercise-b-next';
  nextButton.textContent = 'Next →';
  nextButton.hidden = true; // becomes visible after handleAnswer fires
  nextButton.addEventListener('click', () => {
    // `q=<timestamp>` cache-buster sidesteps the router's id+params
    // dedupe (see top-of-file comment in exercise-a.ts for the gory
    // detail). The screen ignores params.q — its only purpose is to
    // invalidate the dedupe.
    const nextToken = Date.now();
    navigate(`exercise-b?n=${level}&q=${nextToken}`);
  });
  actions.appendChild(nextButton);

  actions.appendChild(renderExitButton());

  section.appendChild(actions);

  mount.appendChild(section);

  // ---- Keyboard handling + auto-cleanup ----
  // See top-of-file comment in exercise-a.ts on the listener
  // lifecycle. A MutationObserver watches the document root and
  // aborts the keydown listener as soon as our `section` is no
  // longer connected — happens on the next route change's
  // mount.replaceChildren() (production) or document.body wipe
  // (tests).
  const controller = new AbortController();
  const observer = new MutationObserver(() => {
    if (!section.isConnected) {
      controller.abort();
      observer.disconnect();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener(
    'keydown',
    (event) => {
      if (answered) return;
      // 1-4 → direct select
      if (event.key >= '1' && event.key <= '9') {
        const idx = Number.parseInt(event.key, 10) - 1;
        if (idx >= 0 && idx < tiles.length) {
          event.preventDefault();
          handleAnswer(idx);
        }
        return;
      }
      // Arrow keys → move focus ring
      if (
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowDown' ||
        event.key === 'ArrowRight'
      ) {
        event.preventDefault();
        const delta = event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
        focusedIndex = clamp(focusedIndex + delta, 0, tiles.length - 1);
        const tile = tiles[focusedIndex];
        if (tile) tile.focus();
        return;
      }
      // Enter → commit focused
      if (event.key === 'Enter') {
        // Only treat Enter as "answer" when an option tile is the
        // active element — otherwise Exit/Next would steal it.
        const active = document.activeElement;
        const tileIdx = tiles.findIndex((t) => t === active);
        if (tileIdx >= 0) {
          event.preventDefault();
          handleAnswer(tileIdx);
        }
      }
    },
    { signal: controller.signal },
  );
}

function parseLevel(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  // Require digit-only input: Number.parseInt would otherwise silently
  // accept "2abc" → 2 and "2.5" → 2, so a hand-edited URL like
  // ?n=2.5 would resolve to level 2 with no signal that the param was
  // malformed. Mirrors parseLevel in exercise-a.ts.
  if (!/^\d+$/.test(raw)) return null;
  const parsed = Number.parseInt(raw, 10);
  if (parsed < 1 || parsed > 8) return null;
  return parsed;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

function renderUnavailable(message: string): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.dataset.testid = 'exercise-b-unavailable';

  const heading = document.createElement('h1');
  heading.textContent = 'No exercise available';
  card.appendChild(heading);

  const body = document.createElement('p');
  body.style.marginTop = 'var(--space-3)';
  body.style.color = 'var(--color-text-warm)';
  body.textContent = message;
  card.appendChild(body);

  return card;
}

function renderExitButton(): HTMLButtonElement {
  // FR-NAV-02 exit affordance: users can always escape back to the
  // level grid, regardless of whether they've answered the current
  // question.
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'btn btn--secondary';
  back.dataset.testid = 'exercise-b-exit';
  back.textContent = 'Back to levels';
  back.addEventListener('click', () => {
    navigate('');
  });
  return back;
}

// Re-export the question shape so test files can type-check
// synthetic fixtures without reaching into src/data.
export type { ExerciseBQuestion };
